import { c as _c } from "react/compiler-runtime";
import { MIN_PLOT_WIDTH, PLOT_INSET } from '../chart-geometry';
import type { KeyboardPlacement } from '@/lib/piano/keyboard/keyboard-geometry';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
export interface ChartFigureProps {
  children: React.ReactNode;
  className?: string;
  /** Legend and toggles, above a rule at its head. */
  header?: React.ReactNode;
  /** Geometry of the keyboard axis, used to place the hover band. */
  geometry?: KeyboardPlacement;
  /** The key being read, marked by a brass band across the plot. */
  highlightedKey?: number | null;
  /** Names the plot for a reader who arrives on it with the keyboard. */
  label?: string;
  /** The drawn keyboard that serves as the x axis. */
  keyboard?: React.ReactNode;
  /** Arrow keys move the reading along the keyboard. */
  onKeyDown?: React.KeyboardEventHandler;
  /** What the key under the pointer reads, along the head of the figure. */
  reading?: React.ReactNode;
  tabIndex?: number;
  variant?: 'analyze' | 'design';
}

/**
 * The frame every chart sits in: a header carrying its legend, the reading of
 * the key under the pointer, and the plot with its keyboard axis.
 */
export const ChartFigure = t0 => {
  const $ = _c(35);
  const {
    children,
    className,
    geometry,
    header,
    highlightedKey,
    keyboard,
    label,
    onKeyDown,
    reading,
    tabIndex,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "analyze" : t1;
  const {
    t
  } = useTranslation();
  const isAnalyze = variant === "analyze";
  let t2;
  if ($[0] !== className) {
    t2 = cn("m-0 min-w-0 rounded-md border border-ptl-rule-strong bg-card", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== header || $[3] !== isAnalyze) {
    t3 = header ? <div className={cn("flex flex-wrap items-center gap-y-2.5 border-b border-border px-[18px] py-3.5", isAnalyze ? "gap-x-[22px]" : "gap-x-[18px]")}>{header}</div> : null;
    $[2] = header;
    $[3] = isAnalyze;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== label || $[6] !== t) {
    t4 = label ?? t("Figure: hover a key, or use the arrow keys");
    $[5] = label;
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = isAnalyze ? "px-3 pt-4" : "px-2.5 pt-3.5";
  let t6;
  if ($[8] !== t5) {
    t6 = cn("overflow-x-auto pb-1.5", "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none", t5);
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = isAnalyze ? MIN_PLOT_WIDTH.analyze : MIN_PLOT_WIDTH.design;
  let t8;
  if ($[10] !== t7) {
    t8 = {
      minWidth: t7
    };
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== geometry || $[13] !== highlightedKey) {
    t9 = geometry && highlightedKey ? <HoverBand geometry={geometry} keyNumber={highlightedKey} /> : null;
    $[12] = geometry;
    $[13] = highlightedKey;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== children) {
    t10 = <div className="relative z-10">{children}</div>;
    $[15] = children;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t9) {
    t11 = <div className="relative">{t9}{t10}</div>;
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== keyboard || $[21] !== t11 || $[22] !== t8) {
    t12 = <div className="relative" style={t8}>{t11}{keyboard}</div>;
    $[20] = keyboard;
    $[21] = t11;
    $[22] = t8;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== onKeyDown || $[25] !== t12 || $[26] !== t4 || $[27] !== t6 || $[28] !== tabIndex) {
    t13 = <div data-slot="chart-plot" role="group" aria-label={t4} onKeyDown={onKeyDown} tabIndex={tabIndex} className={t6}>{t12}</div>;
    $[24] = onKeyDown;
    $[25] = t12;
    $[26] = t4;
    $[27] = t6;
    $[28] = tabIndex;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== reading || $[31] !== t13 || $[32] !== t2 || $[33] !== t3) {
    t14 = <figure data-slot="chart-figure" className={t2}>{t3}{reading}{t13}</figure>;
    $[30] = reading;
    $[31] = t13;
    $[32] = t2;
    $[33] = t3;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
};

/**
 * The brass column behind the key being read.
 *
 * It covers the key's slot at the balance rail — the same column the plot is
 * ruled by — so it stands over the back of the key whose stick it lights, at
 * any width of the figure. It sits behind the plot: a 16 % brass wash over
 * the measurement dots would tint them.
 */
const HoverBand = t0 => {
  const $ = _c(6);
  const {
    geometry,
    keyNumber
  } = t0;
  let t1;
  if ($[0] !== geometry.keysByNumber || $[1] !== keyNumber) {
    t1 = geometry.keysByNumber.get(keyNumber);
    $[0] = geometry.keysByNumber;
    $[1] = keyNumber;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const placed = t1;
  if (!placed || geometry.width === 0) {
    return null;
  }
  const unit = geometry.slotWidth;
  const start = (placed.backCenter - unit / 2) / geometry.width;
  const width = unit / geometry.width;
  const insets = `(100% - ${PLOT_INSET.left + PLOT_INSET.right}px)`;
  const t2 = `calc(${PLOT_INSET.left}px + ${start} * ${insets})`;
  const t3 = `calc(${width} * ${insets})`;
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 z-0 bg-ptl-brass-soft" style={{
      left: t2,
      width: t3
    }} />;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};